import { useCallback, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrowOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/CheckOutlined';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import ArchiveIcon from '@mui/icons-material/ArchiveOutlined';
import { snapshots, type SnapshotArchiveItem } from '../api/client';
import { useData } from '../store/data';
import { useConfig } from '../store/config';
import { fmtInt, formatDateTime } from '../lib/format';

type Props = { open: boolean; onClose: () => void };

export function SnapshotArchiveDialog({ open, onClose }: Props) {
  const [items, setItems] = useState<SnapshotArchiveItem[]>([]);
  const [max, setMax]     = useState<number>(20);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState('');

  const loadData   = useData(s => s.loadFromBackend);
  const loadConfig = useConfig(s => s.loadFromBackend);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await snapshots.list();
      setItems(r.items);
      setMax(r.max);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  async function activate(item: SnapshotArchiveItem) {
    if (!confirm(
      `Snapshot vom ${formatDateTime(item.createdAt * 1000)} („${item.label}") aktivieren?\n\n` +
      `Der aktuelle Stand wird vorher automatisch archiviert.`
    )) return;
    setBusy(true);
    setError(null);
    try {
      await snapshots.activate(item.id);
      // Stores neu laden, damit App sich frisch auf die neuen Rows aufbaut
      await loadData();
      await loadConfig();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(item: SnapshotArchiveItem) {
    if (!confirm(
      `Snapshot „${item.label}" wirklich löschen? Diese Aktion ist endgültig.`
    )) return;
    setBusy(true);
    setError(null);
    try {
      await snapshots.remove(item.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  function startEdit(item: SnapshotArchiveItem) {
    setEditId(item.id);
    setEditLabel(item.label);
  }
  async function saveEdit() {
    if (editId == null) return;
    setBusy(true);
    setError(null);
    try {
      await snapshots.rename(editId, editLabel.trim() || 'ohne Label');
      setEditId(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  function cancelEdit() {
    setEditId(null);
    setEditLabel('');
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth
      slotProps={{ paper: { sx: { borderRadius: 4 } } }}>
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <HistoryIcon color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6">Snapshot-Verlauf</Typography>
            <Typography variant="caption" color="text.secondary">
              Archivierte Import-Stände · max. {max} Snapshots, älteste werden automatisch verworfen
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {busy && <LinearProgress sx={{ mb: 1 }} />}
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {items.length === 0 && !busy && (
          <Alert severity="info">
            Noch keine Snapshots archiviert. Beim nächsten CSV-Import wird der aktuelle Stand automatisch archiviert.
          </Alert>
        )}
        {items.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Zeitpunkt</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Label</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Issues</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map(item => {
                  const isEdit = editId === item.id;
                  return (
                    <TableRow key={item.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12, color: 'text.secondary' }}>
                        {formatDateTime(item.createdAt * 1000)}
                      </TableCell>
                      <TableCell>
                        {isEdit ? (
                          <TextField
                            size="small"
                            value={editLabel}
                            onChange={(e) => setEditLabel(e.target.value)}
                            autoFocus
                            fullWidth
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') void saveEdit();
                              if (e.key === 'Escape') cancelEdit();
                            }}
                          />
                        ) : (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {item.label}
                            </Typography>
                            {item.meta?.file && (
                              <Typography variant="caption" color="text.secondary">
                                aus: {item.meta.file}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {fmtInt(item.issueCount)}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                          {isEdit ? (
                            <>
                              <Tooltip title="Speichern">
                                <IconButton size="small" onClick={() => void saveEdit()} disabled={busy}>
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Abbrechen">
                                <IconButton size="small" onClick={cancelEdit}>
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <Tooltip title="Diesen Snapshot aktivieren">
                                <IconButton size="small" onClick={() => void activate(item)} disabled={busy} color="primary">
                                  <PlayArrowIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Umbenennen">
                                <IconButton size="small" onClick={() => startEdit(item)} disabled={busy}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Löschen">
                                <IconButton size="small" onClick={() => void remove(item)} disabled={busy} color="error">
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          startIcon={<ArchiveIcon />}
          onClick={async () => {
            const label = prompt(
              'Label für den Snapshot (leer lassen für automatisches Datum):'
            );
            if (label === null) return;
            setBusy(true);
            setError(null);
            try {
              await snapshots.create(label.trim() || undefined);
              await load();
            } catch (e) {
              setError(e instanceof Error ? e.message : String(e));
            } finally {
              setBusy(false);
            }
          }}
          disabled={busy}
        >
          Aktuellen Stand archivieren
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={busy}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
}
